/** @file IK layer: chain bones, a link from the chain tip to the target, and a target crosshair. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import type { IkData } from '@pose2d/core/types';
import type { Sink } from '../draw/createSink';
import { emitLine } from '../draw/emitLine';
import { fillSegment } from '../draw/fillSegment';
import { emitRing } from '../draw/emitRing';
import { scaleAlpha } from '../draw/scaleAlpha';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { boneSegment } from './boneSegment';

const seg = new Float64Array(4);
const line = new Float64Array(4);
const tip: [number, number] = [NaN, NaN];
const CROSS = 10;

/** The chain bones as 3 px lines; leaves the last bone's tip in tip[0], tip[1] (NaN for an empty chain). */
function drawChain(sink: Sink, { pose, camera, data }: OverlayInput, ik: IkData, color: number): void {
  tip[0] = NaN;
  tip[1] = NaN;
  for (let j = 0; j < ik.bones.length; j++) {
    const b = ik.bones[j] ?? -1;
    boneSegment(pose, camera, b, data.bones[b]?.length ?? 0, seg);
    emitLine(sink, seg, 3, color);
    tip[0] = seg[2] ?? NaN;
    tip[1] = seg[3] ?? NaN;
  }
}

/** Draws one constraint; alpha follows its current mix (35 % at mix 0, opaque at mix 1). */
function drawConstraint(sink: Sink, input: OverlayInput, ik: IkData, k: number): void {
  const { pose, camera } = input;
  const mix = pose.ik[k * POSE_LAYOUT.IK_STRIDE + POSE_LAYOUT.IK_MIX] ?? ik.mix;
  if (!Number.isFinite(mix)) return; // a non-finite mix would make every primitive fully transparent (R7-03)
  const color = scaleAlpha(OVERLAY_STYLE.ik, 0.35 + 0.65 * Math.min(1, Math.max(0, mix)));
  drawChain(sink, input, ik, color);
  const tipX = tip[0], tipY = tip[1];
  boneSegment(pose, camera, ik.target, 0, seg);
  const cx = seg[0] ?? NaN, cy = seg[1] ?? NaN;
  emitLine(sink, fillSegment(line, tipX, tipY, cx, cy), 1, scaleAlpha(color, 0.6));
  emitRing(sink, cx, cy, 7, 1.5, color);
  emitLine(sink, fillSegment(line, cx - CROSS, cy, cx + CROSS, cy), 1.5, color);
  emitLine(sink, fillSegment(line, cx, cy - CROSS, cx, cy + CROSS), 1.5, color);
}

/** `options.ik`: every IK constraint of the skeleton. */
export function drawIk(sink: Sink, input: OverlayInput): void {
  if (input.options.ik !== true) return;
  for (let k = 0; k < input.data.ik.length; k++) {
    const ik = input.data.ik[k];
    if (ik !== undefined) drawConstraint(sink, input, ik, k);
  }
}
